import { formatVnd } from './format'
import { formatMoneySpeech } from './format-money-speech'
import { m } from './paraglide/messages'
import type {
    StationAudioReason,
    StationAudioState,
} from './station-audio-telemetry'
import type { StationNewOrderEvent } from '@/hooks/use-station-sse'

const DIGIT_TO_VI: Record<string, string> = {
    '0': 'không',
    '1': 'một',
    '2': 'hai',
    '3': 'ba',
    '4': 'bốn',
    '5': 'năm',
    '6': 'sáu',
    '7': 'bảy',
    '8': 'tám',
    '9': 'chín',
}

/**
 * Spell each digit in an order code so VN TTS reads it digit-by-digit
 * instead of mashing it into one giant number. Non-digit characters are
 * preserved verbatim (the TTS engine pronounces letters fine).
 */
export function spelledCode(code: string): string {
    if (!code) return ''
    return code
        .split('')
        .map((c) => DIGIT_TO_VI[c] ?? c)
        .join(' ')
}

/** Customer name for announcements, falling back to the walk-in label. */
function announcementCustomerName(event: StationNewOrderEvent): string {
    // Coerce defensively: a malformed SSE payload missing customer_name would
    // otherwise throw inside the SSE handler's try/catch and silently drop the
    // whole announcement (no toast, no TTS).
    return (event.customer_name ?? '').trim() || m.customer_walk_in()
}

/** Visual toast: amount is rendered with the grouped-thousands VND format. */
export function buildNewOrderToast(event: StationNewOrderEvent): string {
    return m.station_new_order_toast({
        customerName: announcementCustomerName(event),
        amount: formatVnd(event.amount ?? 0),
    })
}

/** Giá trị điền vào template lời thoại máy trạm (nguồn: payload SSE). */
export interface SpeechTemplateValues {
    ma_don?: string
    ma_don_noi_bo?: string
    ten_khach?: string
    sdt?: string
    so_tien?: number
    nguoi_tao?: string
    nguoi_giao?: string
    /** Người TẠO phiếu xuất kho của đơn (FD-383) — khác nguoi_tao (tạo đơn). */
    ten_nguoi_tao_phieu_xuat?: string
    /** Người DUYỆT phiếu xuất kho của đơn (FD-383). */
    ten_nguoi_duyet_phieu_xuat?: string
    noi_dung?: string
    ma_may?: string
    thoi_gian_con_lai?: number
    ten_san_pham?: string
    so_luong_con_lai?: number
    /** Số CÒN NỢ của một khoản nợ lô (FD-372), khác so_luong_con_lai là tồn kho. */
    so_luong?: number
    don_vi?: string
    /** Tên cuốn sổ vừa có mẻ mới (#M-16), vd "đóng hộp". */
    ten_so?: string
    /** Người ghi sổ (#M-16) — khác nguoi_tao vốn là người tạo ĐƠN. */
    nguoi_ghi?: string
}

/**
 * Số tồn đọc loa: tồn lưu dạng NUMERIC(15,3) nên 17 về tới đây có thể là 17.000
 * — đọc "mười bảy phẩy không không không" thì nghe rất tệ. Cắt số 0 thừa nhưng
 * giữ phần lẻ có nghĩa (2.5 vẫn là 2.5).
 */
function formatQuantitySpeech(qty: number): string {
    return String(Number(qty.toFixed(3)))
}

/**
 * Điền placeholder trong template cấu hình thành câu đọc. Mã đơn / mã nội bộ /
 * SĐT đọc từng chữ số; số tiền đọc thành chữ; tên khách rỗng → "khách lẻ";
 * người tạo / tài xế giao / người tạo - duyệt phiếu xuất kho đọc nguyên văn.
 * Token lạ giữ nguyên; giá trị thiếu → chuỗi rỗng.
 */
export function renderSpeechTemplate(
    template: string,
    values: SpeechTemplateValues,
): string {
    return template
        .replaceAll('{ma_don}', values.ma_don ? spelledCode(values.ma_don) : '')
        .replaceAll(
            '{ma_don_noi_bo}',
            values.ma_don_noi_bo ? spelledCode(values.ma_don_noi_bo) : '',
        )
        .replaceAll(
            '{ten_khach}',
            (values.ten_khach ?? '').trim() || m.customer_walk_in(),
        )
        .replaceAll('{sdt}', values.sdt ? spelledCode(values.sdt) : '')
        .replaceAll(
            '{so_tien}',
            values.so_tien != null ? formatMoneySpeech(values.so_tien) : '',
        )
        .replaceAll('{nguoi_tao}', (values.nguoi_tao ?? '').trim())
        .replaceAll('{nguoi_giao}', (values.nguoi_giao ?? '').trim())
        .replaceAll(
            '{ten_nguoi_tao_phieu_xuat}',
            (values.ten_nguoi_tao_phieu_xuat ?? '').trim(),
        )
        .replaceAll(
            '{ten_nguoi_duyet_phieu_xuat}',
            (values.ten_nguoi_duyet_phieu_xuat ?? '').trim(),
        )
        .replaceAll('{noi_dung}', (values.noi_dung ?? '').trim())
        .replaceAll('{ten_so}', (values.ten_so ?? '').trim())
        .replaceAll('{nguoi_ghi}', (values.nguoi_ghi ?? '').trim())
        .replaceAll('{ma_may}', (values.ma_may ?? '').trim())
        .replaceAll(
            '{thoi_gian_con_lai}',
            values.thoi_gian_con_lai != null
                ? String(values.thoi_gian_con_lai)
                : '',
        )
        .replaceAll('{ten_san_pham}', (values.ten_san_pham ?? '').trim())
        .replaceAll(
            '{so_luong_con_lai}',
            values.so_luong_con_lai != null
                ? formatQuantitySpeech(values.so_luong_con_lai)
                : '',
        )
        .replaceAll(
            '{so_luong}',
            values.so_luong != null
                ? formatQuantitySpeech(values.so_luong)
                : '',
        )
        .replaceAll('{don_vi}', (values.don_vi ?? '').trim())
}

// Setting `utterance.lang` alone is not enough on Chrome/Edge — without an
// explicit `voice`, the engine often falls back to the system default
// (English), which then reads Vietnamese characters phonetically as English.
export function pickVietnameseVoice(): SpeechSynthesisVoice | null {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
        return null
    }
    const voices = speechSynthesis.getVoices()
    return voices.find((v) => v.lang.toLowerCase().startsWith('vi')) ?? null
}

export function warmUpTts(): void {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return
    const warmup = new SpeechSynthesisUtterance('')
    warmup.volume = 0
    speechSynthesis.speak(warmup)
}

/** Simple two-tone chime using Web Audio API (no external file needed). */
export function playBeepChime(
    report?: (state: StationAudioState, reason?: StationAudioReason) => void,
): void {
    const notify = (state: StationAudioState, reason?: StationAudioReason) => {
        try {
            if (reason) report?.(state, reason)
            else report?.(state)
        } catch {
            /* Observers cannot stop playback. */
        }
    }
    if (typeof AudioContext === 'undefined') {
        notify('unsupported', 'missing_browser_api')
        return
    }
    try {
        const ctx = new AudioContext()
        let observedStart = false
        const observeState = () => {
            if (ctx.state === 'suspended') notify('blocked', 'suspended')
        }
        const playTone = (
            freq: number,
            start: number,
            duration: number,
            last = false,
        ) => {
            const osc = ctx.createOscillator()
            const gain = ctx.createGain()
            osc.connect(gain)
            gain.connect(ctx.destination)
            osc.frequency.value = freq
            osc.type = 'sine'
            gain.gain.setValueAtTime(0.3, ctx.currentTime + start)
            gain.gain.exponentialRampToValueAtTime(
                0.01,
                ctx.currentTime + start + duration,
            )
            osc.onended = () => {
                if (ctx.state !== 'running') return
                // Web Audio has no source start event. Ending the first tone
                // proves playback advanced; scheduling alone does not.
                if (!observedStart) {
                    observedStart = true
                    notify('started')
                }
                if (last) notify('finished')
            }
            osc.start(ctx.currentTime + start)
            osc.stop(ctx.currentTime + start + duration)
        }
        playTone(880, 0, 0.15)
        playTone(1320, 0.18, 0.25, true)
        ctx.onstatechange = observeState
        observeState()
        setTimeout(() => {
            ctx.onstatechange = null
            void ctx.close()?.catch(() => {})
        }, 1000)
    } catch (error) {
        const blocked =
            typeof error === 'object' &&
            error !== null &&
            'name' in error &&
            error.name === 'NotAllowedError'
        notify(
            blocked ? 'blocked' : 'error',
            blocked ? 'not_allowed' : 'audio_failed',
        )
    }
}

interface KeepaliveOptions {
    /**
     * Interval between pause/resume cycles. Chrome's speechSynthesis silently
     * stops responding after ~5 minutes of inactivity; periodic pause→resume
     * keeps the engine alive. Default 10s.
     */
    intervalMs?: number
    /** Injection seam for tests. */
    getSpeechSynthesis?: () => SpeechSynthesis | null
}

export interface TtsKeepalive {
    start: () => void
    stop: () => void
}

/**
 * Returns a controllable timer that fires `pause(); resume()` on the
 * SpeechSynthesis singleton at a steady interval. The trick is well-known
 * (issue.chromium.org/issues/40410893) and prevents the queue from going
 * silent after the tab has been open for hours.
 */
export function createTtsKeepalive(opts: KeepaliveOptions = {}): TtsKeepalive {
    const intervalMs = opts.intervalMs ?? 10_000
    const resolve =
        opts.getSpeechSynthesis ??
        (() =>
            typeof window !== 'undefined' && 'speechSynthesis' in window
                ? window.speechSynthesis
                : null)
    let handle: ReturnType<typeof setInterval> | null = null

    return {
        start() {
            if (handle != null) return
            const synth = resolve()
            if (!synth) return
            handle = setInterval(() => {
                try {
                    synth.pause()
                    synth.resume()
                } catch {
                    /* engine may be torn down mid-interval; ignore. */
                }
            }, intervalMs)
        },
        stop() {
            if (handle == null) return
            clearInterval(handle)
            handle = null
        },
    }
}
